import * as React from "react";
import { useState } from "react";
import type { VorgangPickerItem } from "../../../services/vorgangService";
import VorgangPickerAuswahl from "../picker/VorgangPickerAuswahl";
import { VorgaengeZusammenfuehrenDialog } from "../VorgaengeZusammenfuehrenDialog";
import { IcoX } from "../v3/vg3Icons";
// `VorgangPickerAuswahl` holt seine Optik aus `.cal-vgd-*` in calendar.css.
import "../../calendar/calendar.css";
import "../v3/vorgangV3.css";

type PickerVorgang = VorgangPickerItem["vorgang"];

/**
 * „Mit anderem Vorgang zusammenführen…" aus der Detailansicht (PO 28.09.2026:
 * „Zusammenführen?").
 *
 * Bis dahin ließ sich ein Vorgang NUR aus der Liste zusammenführen — per
 * Ziehen oder über die Mehrfachauswahl. In der Detailansicht, wo man merkt,
 * dass es die Sache schon gibt, fehlte der Weg; mit To-dos stand dort nicht
 * einmal ein ⋯.
 *
 * Bewusst KEIN eigener Ablauf: Nach der Wahl des zweiten Vorgangs öffnet
 * derselbe Dialog wie in der Liste (`VorgaengeZusammenfuehrenDialog`) — wer
 * bleibt, was wandert, „als To-do" wo es erzwungen ist
 * (`zusammenfuehrWege.ts`). Zwei Oberflächen für dieselbe Entscheidung
 * liefen sonst früher oder später auseinander.
 */
type Props = {
    vorgang: { id: number; titel: string; vorgangNbr?: string | null; createdAt?: string | null };
    contactId?: number | null;
    onClose: () => void;
    /** Wie `VorgaengeZusammenfuehrenDialog.onFertig`. */
    onFertig: (zielId: number, verschwundeneIds: number[]) => void;
};

export const VorgangZusammenfuehrenMitDialog: React.FC<Props> = ({ vorgang, contactId = null, onClose, onFertig }) => {
    const [anderer, setAnderer] = useState<PickerVorgang | null>(null);

    if (anderer) {
        return (
            <VorgaengeZusammenfuehrenDialog
                kandidaten={[
                    {
                        id: vorgang.id,
                        titel: vorgang.titel,
                        vorgangNbr: vorgang.vorgangNbr ?? null,
                        createdAt: vorgang.createdAt ?? null,
                    },
                    {
                        id: anderer.id,
                        titel: anderer.titel,
                        vorgangNbr: anderer.vorgangNbr,
                        createdAt: anderer.createdAt,
                    },
                ]}
                onClose={onClose}
                onFertig={onFertig}
            />
        );
    }

    return (
        <div className="kvp-rd vg3-modal-hg" role="presentation" onClick={onClose}>
            <div
                className="vg3-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Mit anderem Vorgang zusammenführen"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">Mit anderem Vorgang zusammenführen</span>
                    <button type="button" className="vg3-ibtn" title="Schließen" aria-label="Schließen" onClick={onClose}>
                        <IcoX size={16} />
                    </button>
                </div>
                <div className="vg3-modal-b">
                    <p className="vg3-modal-hinweis">
                        Mit welchem Vorgang? Danach wählen Sie, welcher bestehen bleibt und was mitwandert.
                    </p>
                    <VorgangPickerAuswahl onPick={setAnderer} contactId={contactId} aktuelleVorgangId={vorgang.id} />
                </div>
            </div>
        </div>
    );
};

export default VorgangZusammenfuehrenMitDialog;
